import { Controller } from "@hotwired/stimulus";

/**
 * Contrôleur pour le formulaire d'écriture comptable
 *
 * Gère les fonctionnalités dynamiques du formulaire:
 * - Ajout/suppression de lignes d'écriture
 * - Calcul des totaux
 * - Vérification de l'équilibre débit/crédit
 * - Gestion des paramètres de récurrence
 * - Prévisualisation des pièces jointes
 */
export default class extends Controller {
  static targets = [
    "linesContainer",
    "lineTemplate",
    "lineRow",
    "nextLineId",
    "debitTotal",
    "creditTotal",
    "balanceStatus",
    "submitButton",
    "recurringFields",
    "recurringCheckbox",
    "templateFields",
    "templateCheckbox",
    "fileInput",
    "filePreview",
    "fiscalYear",
    "entryDate",
  ];

  static values = {
    nextLineId: Number,
    debitTotal: Number,
    creditTotal: Number,
    // ⚠ Ces trois valeurs viennent de la VUE, traduites par Rails. Elles
    // étaient écrites en dur en français dans ce contrôleur : le bloc Totaux
    // affichait « Équilibré » et « Déséquilibré (différence : …) » sous une
    // interface anglaise, et les montants étaient formatés en `fr-CM` quelle
    // que soit la langue.
    balancedLabel: String,
    unbalancedLabel: String,
    locale: String,
  };

  // La locale du document, avec repli sur le français : c'est elle qui décide
  // du séparateur de milliers et de la place du symbole monétaire.
  get intlLocale() {
    return this.hasLocaleValue && this.localeValue
      ? this.localeValue
      : document.documentElement.lang || "fr";
  }

  formatCurrency(value) {
    return new Intl.NumberFormat(this.intlLocale, {
      style: "currency",
      currency: "XAF",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  connect() {
    // Initialiser les compteurs

    if (this.hasNextLineIdTarget) {
      this.nextLineIdValue = this.nextLineIdTarget.value;
    } else {
      this.nextLineIdValue = this.nextLineIdValue || 1;
    }
    this.debitTotalValue = 0;
    this.creditTotalValue = 0;

    // Si aucune ligne n'existe, en ajouter une vide
    if (this.hasLineRowTargets && this.lineRowTargets.length === 0) {
      this.addLine();
    }

    // Calculer les totaux initiaux
    this.calculateTotals();

    // Initialiser les champs de récurrence et de modèle
    this.toggleRecurringFields();
    this.toggleTemplateFields();

    // Initialiser la prévisualisation des fichiers
    this.setupFilePreview();
  }

  addLine(event) {
    if (event) event.preventDefault();

    // Cloner le template
    const content = this.lineTemplateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      this.nextLineIdValue,
    );

    // Ajouter au conteneur
    this.linesContainerTarget.insertAdjacentHTML("beforeend", content);

    // Incrémenter l'ID pour la prochaine ligne
    this.nextLineIdValue++;

    // Recalculer les totaux
    this.calculateTotals();
  }

  removeLine(event) {
    event.preventDefault();

    const line = event.target.closest(".entry-line");

    // Vérifier s'il s'agit d'un enregistrement existant
    const destroyField = line.querySelector("input[name*='_destroy']");

    if (destroyField) {
      // Marquer pour suppression
      destroyField.value = "1";
      line.classList.add("opacity-50");
      line.classList.add("hidden");
    } else {
      // Supprimer du DOM
      line.remove();
    }

    // Recalculer les totaux
    this.calculateTotals();
  }

  calculateTotals() {
    if (!this.hasLineRowTarget) return;

    let debitTotal = 0;
    let creditTotal = 0;

    this.lineRowTargets.forEach((line) => {
      if (line.classList.contains("hidden")) return;

      const debitInput = line.querySelector("input[name*='[debit_amount]']");
      const creditInput = line.querySelector("input[name*='[credit_amount]']");

      if (debitInput && debitInput.value) {
        debitTotal += parseFloat(debitInput.value) || 0;
      }

      if (creditInput && creditInput.value) {
        creditTotal += parseFloat(creditInput.value) || 0;
      }
    });

    // Mettre à jour les valeurs
    this.debitTotalValue = debitTotal;
    this.creditTotalValue = creditTotal;

    console.log(this.debitTotalTarget, this.creditTotalTarget);

    // Mettre à jour l'affichage
    if (this.hasDebitTotalTarget) {
      this.debitTotalTarget.textContent = new Intl.NumberFormat(this.intlLocale, {
        style: "currency",
        currency: "XAF",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(debitTotal);
    }

    if (this.hasCreditTotalTarget) {
      this.creditTotalTarget.textContent = new Intl.NumberFormat(this.intlLocale, {
        style: "currency",
        currency: "XAF",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(creditTotal);
    }

    // Vérifier l'équilibre
    this.checkBalance();
  }

  checkBalance() {
    if (!this.hasBalanceStatusTarget) return;
    const difference = Math.abs(this.debitTotalValue - this.creditTotalValue);
    const balanced = difference < 0.01;

    if (balanced) {
      this.balanceStatusTarget.textContent =
        this.hasBalancedLabelValue ? this.balancedLabelValue : "Équilibré";
      this.balanceStatusTarget.classList.remove("text-red-500");
      this.balanceStatusTarget.classList.add("text-cta");
      // this.submitButtonTarget.disabled = false;
    } else {
      const gabarit = this.hasUnbalancedLabelValue
        ? this.unbalancedLabelValue
        : "Déséquilibré (différence: %{amount})";
      this.balanceStatusTarget.textContent = gabarit.replace(
        "%{amount}",
        this.formatCurrency(difference),
      );
      this.balanceStatusTarget.classList.remove("text-cta");
      this.balanceStatusTarget.classList.add("text-red-500");
      // Ne pas désactiver le bouton pour brouillon
      // this.submitButtonTarget.disabled = true;
    }
  }

  toggleAmount(event) {
    // Une ligne ne peut pas avoir à la fois un débit et un crédit
    const row = event.target.closest(".entry-line");
    const isDebit = event.target.name.includes("[debit_amount]");

    const otherField = isDebit
      ? row.querySelector("input[name*='[credit_amount]']")
      : row.querySelector("input[name*='[debit_amount]']");

    if (event.target.value && event.target.value > 0) {
      otherField.value = "";
      otherField.disabled = true;
    } else {
      otherField.disabled = false;
    }

    this.calculateTotals();
  }

  toggleRecurringFields() {
    if (!this.hasRecurringCheckboxTarget || !this.hasRecurringFieldsTarget)
      return;

    if (this.recurringCheckboxTarget.checked) {
      this.recurringFieldsTarget.classList.remove("hidden");
    } else {
      this.recurringFieldsTarget.classList.add("hidden");
    }
  }

  toggleTemplateFields() {
    if (!this.hasTemplateCheckboxTarget || !this.hasTemplateFieldsTarget)
      return;

    if (this.templateCheckboxTarget.checked) {
      this.templateFieldsTarget.classList.remove("hidden");
      // Si c'est un modèle, désactiver les champs de récurrence
      if (this.hasRecurringCheckboxTarget) {
        this.recurringCheckboxTarget.checked = false;
        this.recurringCheckboxTarget.disabled = true;
        this.toggleRecurringFields();
      }
    } else {
      this.templateFieldsTarget.classList.add("hidden");
      // Réactiver les champs de récurrence
      if (this.hasRecurringCheckboxTarget) {
        this.recurringCheckboxTarget.disabled = false;
      }
    }
  }

  setupFilePreview() {
    console.log(this.hasFileInputTarget, this.hasFilePreviewTarget);
    if (!this.hasFileInputTarget || !this.hasFilePreviewTarget) return;

    this.fileInputTarget.addEventListener("change", (event) => {
      this.filePreviewTarget.innerHTML = "";

      const files = event.target.files;

      if (files.length === 0) return;

      Array.from(files).forEach((file) => {
        const fileItem = document.createElement("div");
        fileItem.classList.add(
          "flex",
          "items-center",
          "p-2",
          "bg-gray-100",
          "rounded",
          "my-1",
        );

        // Icône adaptée au type de fichier
        let iconClass = "fas fa-file";
        if (file.type.includes("image")) {
          iconClass = "fas fa-file-image";
        } else if (file.type.includes("pdf")) {
          iconClass = "fas fa-file-pdf";
        } else if (
          file.type.includes("excel") ||
          file.type.includes("spreadsheet")
        ) {
          iconClass = "fas fa-file-excel";
        } else if (
          file.type.includes("word") ||
          file.type.includes("document")
        ) {
          iconClass = "fas fa-file-word";
        }

        fileItem.innerHTML = `
          <i class="${iconClass} mr-2"></i>
          <span>${file.name}</span>
          <span class="text-xs text-gray-500 ml-2">(${(
            file.size / 1024
          ).toFixed(1)} KB)</span>
        `;

        this.filePreviewTarget.appendChild(fileItem);
      });
    });
  }

  fiscalYearChanged(event) {
    if (!this.hasEntryDateTarget) return;

    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];

    if (!selectedOption) return;

    const startDate = selectedOption.dataset.startDate;
    const endDate = selectedOption.dataset.endDate;

    if (startDate && endDate) {
      const flatpickrInput = this.entryDateTarget;
      // Access flatpickr instance attached to the element
      const fp = flatpickrInput._flatpickr;

      if (fp) {
        // Update min and max dates
        fp.set("minDate", startDate);
        fp.set("maxDate", endDate);

        // Check if current date is within range
        const currentDate = fp.selectedDates[0];
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Adjust date if outside range or not set
        if (!currentDate || currentDate < start || currentDate > end) {
          fp.setDate(start);
        }
      }
    }
  }
}
